import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity as ActivityIcon,
  AlertOctagon,
  CalendarClock,
  Contact2,
  Database,
  FileBarChart,
  FolderOpen,
  Gauge,
  GraduationCap,
  History,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Megaphone,
  Menu,
  MessagesSquare,
  Moon,
  ReceiptIndianRupee,
  SlidersHorizontal,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  Sun,
  Trophy,
  UserCheck,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AccentPicker } from "@/components/AccentPicker";
import { NoticeBell } from "@/components/NoticeBell";
import { NoticeBand } from "@/components/NoticeBand";
import { Logos } from "@/components/Logos";
import { MissionTicker } from "@/components/MissionTicker";
import { AssistantPanel } from "@/components/AssistantPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    testId: "nav-dashboard",
    screen: "dashboard",
  },
  {
    to: "/leaderboard",
    label: "Leaderboard",
    icon: Trophy,
    testId: "nav-leaderboard",
    screen: "leaderboard",
  },
  { to: "/projects", label: "Projects", icon: ListChecks, testId: "nav-projects", screen: "projects" },
  {
    to: "/inconsistency",
    label: "Data Inconsistency",
    icon: AlertOctagon,
    testId: "nav-inconsistency",
    screen: "inconsistency",
  },
  {
    to: "/compliance-data",
    label: "Compliance Data",
    icon: Gauge,
    testId: "nav-compliance-data",
    screen: "compliance_data",
  },
  {
    to: "/revisions",
    label: "Date Revisions",
    icon: History,
    testId: "nav-revisions",
    screen: "revisions",
  },
  {
    to: "/training",
    label: "Training Status",
    icon: GraduationCap,
    testId: "nav-training",
    screen: "training",
  },
  {
    to: "/meetings",
    label: "Meeting Cadence",
    icon: CalendarClock,
    testId: "nav-meetings",
    screen: "meetings",
  },
  { to: "/reports", label: "Reports", icon: FileBarChart, testId: "nav-reports", screen: "reports" },
  {
    to: "/people",
    label: "People",
    icon: Contact2,
    testId: "nav-people",
    screen: "people",
  },
  {
    to: "/verification",
    label: "Verification",
    icon: ShieldQuestion,
    testId: "nav-verification",
    screen: "verification",
    verificationOnly: true,
  },
  {
    to: "/activity",
    label: "Activity",
    icon: ActivityIcon,
    testId: "nav-activity",
    screen: "activity",
    activityOnly: true,
  },
  {
    to: "/invoices",
    label: "Invoice Tracker",
    icon: ReceiptIndianRupee,
    testId: "nav-invoices",
    screen: "invoices",
  },
  {
    to: "/cockpit",
    label: "Control cockpit",
    icon: SlidersHorizontal,
    testId: "nav-cockpit",
    cockpitOnly: true,
  },
  {
    to: "/approvers",
    label: "Approvers",
    icon: UserCheck,
    testId: "nav-approvers",
    screen: "approvers",
  },
  {
    to: "/board",
    label: "Notice board",
    icon: Megaphone,
    testId: "nav-board",
    screen: "board",
    ownerOnly: true,
  },
  {
    to: "/forum",
    label: "Discussion Forum",
    icon: MessagesSquare,
    testId: "nav-forum",
    screen: "forum",
    hideForPartner: true,
  },
  { to: "/profile", label: "My profile", icon: UserRound, testId: "nav-profile", screen: "profile" },  {
    to: "/data",
    label: "Data files",
    icon: Database,
    testId: "nav-data",
    screen: "data",
  },
  {
    to: "/library",
    label: "Important Files",
    icon: FolderOpen,
    testId: "nav-library",
    screen: "library",
  },
  {
    to: "/admin/settings",
    label: "Reminders & digest",
    icon: SettingsIcon,
    testId: "nav-settings",
    screen: "settings",
    adminOnly: true,
  },
];

export const AppShell = ({ children }) => {
  const {
    user,
    logout,
    isAdmin,
    canUpload,
    isPartner,
    isManagement,
    canVerify,
    canSeeActivity,
    sees,
    shows,
    curtained,
    cockpitOwner,
    isDesignated,
    roleLabel,
    superAdmin,
    superAdminEligible,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // The portal opens dark; the officer's own choice is remembered once they use the toggle.
  const [dark, setDark] = useState(() => localStorage.getItem("sail_dx_theme") !== "light");
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sail_dx_nav") === "collapsed",
  );
  const [askOpen, setAskOpen] = useState(false);
  const [waiting, setWaiting] = useState(0);
      const seesQueue = canVerify || isDesignated || isAdmin || isPartner;

  useEffect(() => {
    localStorage.setItem("sail_dx_nav", collapsed ? "collapsed" : "open");
  }, [collapsed]);

  // How many changes are waiting to be verified — read on every screen change, and every minute.
  useEffect(() => {
    if (!seesQueue) return undefined;
    let alive = true;
    const read = () =>
      api
        .get("/pending/count")
        .then((r) => alive && setWaiting(r.data?.awaiting || 0))
        .catch(() => {});
    read();
    const timer = setInterval(read, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [seesQueue, location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("sail_dx_theme", dark ? "dark" : "light");
  }, [dark]);

  const items = NAV.filter(
    (n) =>
      (n.cockpitOnly
        ? cockpitOwner
        : n.ownerOnly
          ? superAdminEligible && sees(n.screen)
          : sees(n.screen)) &&
      (!n.adminOnly || isAdmin) &&
      (!n.uploaderOnly || canUpload) &&
      (!n.hideForPartner || !isPartner) &&
      (!n.hideForManagement || !isManagement) &&
      (!n.activityOnly || canSeeActivity) &&
      (!n.verificationOnly || canVerify || isDesignated || isAdmin || isPartner),
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="no-print sticky top-0 z-40 border-b border-border bg-card">
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              className="lg:hidden"
              onClick={() => setOpen((v) => !v)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="nav-collapse-toggle"
              title={collapsed ? "Show the menu labels" : "Collapse the menu"}
              className="hidden text-muted-foreground transition-colors duration-200 hover:text-foreground lg:block"
              onClick={() => setCollapsed((v) => !v)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" className="flex items-center gap-3">
              <Logos boxed size="h-8" />
              <span className="leading-tight">
                <span className="block font-display text-sm font-black uppercase tracking-[0.16em]">
                  SAIL · DX Status Portal
                </span>
                <span className="label-caps hidden sm:block">Pravartanam Digitalisation Initiative</span>
              </span>
            </Link>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="mr-2 hidden text-right sm:block">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="label-caps">
                {roleLabel}
                {user?.plant ? ` · ${user.plant}` : ""}
              </div>
            </div>
            <NoticeBell />
            <AccentPicker />
            <Button
              variant="outline"
              size="icon"
              className="rounded-none"
              data-testid="theme-toggle"
              onClick={() => setDark((v) => !v)}
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              data-testid="logout-button"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        <MissionTicker />
        <NoticeBand where="portal" />
        {superAdmin && (
          <Link
            to="/profile"
            data-testid="super-admin-banner"
            className="flex items-center justify-center gap-2 bg-[hsl(var(--at-risk))] px-4 py-1.5 text-center text-[11px] font-bold uppercase tracking-widest text-white transition-opacity hover:opacity-90"
          >
            <ShieldAlert className="h-3.5 w-3.5" />
            Super-Admin mode is on · you can change anything and nothing is being recorded · switch
            it off in My profile
          </Link>
        )}
      </header>

      <div className="flex">
        <aside
          className={`no-print ${open ? "block" : "hidden"} fixed inset-x-0 top-[61px] z-30 max-h-[calc(100vh-61px)] overflow-y-auto border-b border-border bg-card transition-[width] duration-200 lg:sticky lg:top-[61px] lg:block lg:h-[calc(100vh-61px)] ${
            collapsed ? "lg:w-16" : "lg:w-60"
          } lg:shrink-0 lg:border-b-0 lg:border-r`}
        >
          <nav className="flex flex-col p-3 lg:p-4">
            {items.map((n) => {
              const active =
                n.to === "/"
                  ? location.pathname === "/" || location.pathname === "/dashboard"
                  : location.pathname.startsWith(n.to);
              const soon = curtained(n.screen);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  title={n.label}
                  onClick={() => setOpen(false)}
                  className={`relative flex items-center gap-3 border-l-2 px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                    collapsed ? "lg:justify-center lg:gap-0 lg:px-0" : ""
                  } ${
                    active
                      ? "border-primary bg-muted text-foreground"
                      : "border-transparent text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <n.icon className="h-4 w-4 shrink-0" />
                  <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
                  {soon && (
                    <span
                      data-testid={`nav-soon-${n.screen}`}
                      title="Built, but SDTD has not opened it yet"
                      className={`font-data ml-auto border border-border px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider text-muted-foreground ${
                        collapsed ? "lg:hidden" : ""
                      }`}
                    >
                      soon
                    </span>
                  )}
                  {n.testId === "nav-verification" && waiting > 0 && (
                    <span
                      data-testid="nav-verification-badge"
                      title={
                        canVerify
                          ? `${waiting} change(s) waiting for your verification`
                          : isPartner
                            ? `${waiting} change(s) waiting for the Pravartanam Partner Manager`
                            : `${waiting} change(s) waiting for approval`
                      }
                      className={`font-data ml-auto border border-[hsl(36_92%_44%)] bg-[hsl(36_92%_44%/0.14)] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[hsl(36_92%_44%)] ${
                        collapsed ? "lg:absolute lg:right-1.5 lg:top-1.5 lg:ml-0 lg:px-1" : ""
                      }`}
                    >
                      {waiting > 99 ? "99+" : waiting}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">{children}</main>
      </div>

      {shows("dashboard.assistant") && (
        <>
          <button
            type="button"
            data-testid="open-assistant"
            onClick={() => setAskOpen(true)}
            className="no-print fixed bottom-6 right-6 z-30 flex items-center gap-2 border border-border bg-foreground px-4 py-3 font-display text-xs font-bold uppercase tracking-widest text-background shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" /> Ask
          </button>
          <AssistantPanel open={askOpen} onClose={() => setAskOpen(false)} />
        </>
      )}
    </div>
  );
};
